/**
 * Work Sample & Compliance Workflow — client constants for org app_assignment metadata.
 * Mirrors api/constants/workSample.js (workflow key, role registry shape).
 */

export const WORK_SAMPLE_WORKFLOW_KEY = 'work_sample_compliance';

export const WORK_SAMPLE_ROLES = Object.freeze({
  STUDENT: 'student',
  TOR: 'tor',
  VDM: 'vdm',
  COMPLIANCE: 'compliance',
  VENDOR: 'vendor'
});

export const TEACHER_OF_RECORD_LINK_TYPE = 'teacher_of_record';
export {
  FORM_RELATIONSHIP_TYPES,
  WORK_SAMPLE_FORM_ASSIGNMENT_TYPE
} from './formRelationships';
export const VENDOR_PARTNER_ASSOCIATION_TYPE = 'vendor_partner';

/** TOR correction window after a VDM flag (weekdays). UI-derived only — no worker yet. */
export const CORRECTION_SLA_BUSINESS_DAYS = 2;

/** Prefix on roles.*.gp_id — match the gp or any nested team under it. */
export const GP_ROLE_DESCENDANTS_PREFIX = '*descendants*';

export function parseGpRoleRef(raw) {
  const s = String(raw ?? '').trim();
  if (!s) return { gpId: null, includeDescendants: false, encoded: null };
  if (s.startsWith(GP_ROLE_DESCENDANTS_PREFIX)) {
    const gpId = s.slice(GP_ROLE_DESCENDANTS_PREFIX.length).trim();
    if (!gpId) return { gpId: null, includeDescendants: true, encoded: null };
    return {
      gpId,
      includeDescendants: true,
      encoded: `${GP_ROLE_DESCENDANTS_PREFIX}${gpId}`
    };
  }
  return { gpId: s, includeDescendants: false, encoded: s };
}

export function encodeGpRoleRef(gpId, { includeDescendants = false } = {}) {
  const id = String(gpId ?? '').trim();
  if (!id) return null;
  return includeDescendants ? `${GP_ROLE_DESCENDANTS_PREFIX}${id}` : id;
}

/** Roles that pick a group-period in the work-sample assignment metadata editor. */
export const WORK_SAMPLE_GP_ROLE_FIELDS = Object.freeze([
  {
    roleKey: WORK_SAMPLE_ROLES.STUDENT,
    label: 'Students',
    hint: 'Enrolled students whose form submissions and documents can be curated into work samples.',
    required: true
  },
  {
    roleKey: WORK_SAMPLE_ROLES.VDM,
    label: 'VDM / Tech Support',
    hint: 'Team that reviews work samples and runs the 6-point compliance check.',
    required: true
  },
  {
    roleKey: WORK_SAMPLE_ROLES.COMPLIANCE,
    label: 'Compliance',
    hint: 'Second approval on vouchers (e-sign).',
    required: true
  }
]);

export function formatGroupsPeriodLabel(gp) {
  if (!gp) return '';
  const g = gp.group_name || 'Group';
  const p = gp.period_title || 'Period';
  return `${g} — ${p}`;
}

export function isOrgShellGroupType(groupType) {
  const t = String(groupType || '').toLowerCase();
  return t === 'organization' || t === 'system';
}

export function isOrgShellGp(gp) {
  return Boolean(gp && isOrgShellGroupType(gp.group_type));
}

function collectGpIdsFromNestedGroups(nestedGroups) {
  const ids = new Set();
  (nestedGroups || []).forEach((group) => {
    (group.periods || []).forEach((p) => {
      if (p?.id) ids.add(String(p.id));
    });
  });
  return ids;
}

export function flattenOrgTeamGpOptions(nestedGroups) {
  const options = [];
  const seen = new Set();
  (nestedGroups || []).forEach((group) => {
    if (isOrgShellGroupType(group.group_type)) return;
    (group.periods || []).forEach((period) => {
      const id = period?.id != null ? String(period.id) : '';
      if (!id || seen.has(id)) return;
      seen.add(id);
      options.push({
        id,
        group_name: group.name || '',
        period_title: period.period_title || period.title || ''
      });
    });
  });
  return options.sort((a, b) =>
    formatGroupsPeriodLabel(a).localeCompare(formatGroupsPeriodLabel(b))
  );
}

export function mergeGpOptionsWithSelections(options, groupsPeriods, selectedIds) {
  const byId = new Map((options || []).map((o) => [String(o.id), o]));
  (selectedIds || []).forEach((id) => {
    const sid = String(id || '').trim();
    if (!sid || byId.has(sid)) return;
    const gp = (groupsPeriods || []).find((g) => String(g.id) === sid);
    if (gp) byId.set(sid, gp);
  });
  return [...byId.values()].sort((a, b) =>
    formatGroupsPeriodLabel(a).localeCompare(formatGroupsPeriodLabel(b))
  );
}

async function fetchNestedGroupsForOrg(orgGpId) {
  const r = await fetch(
    `/api/relationships/nested-groups?superparent_groups_periods_id=${encodeURIComponent(orgGpId)}`
  );
  const d = await r.json();
  if (!d.success) throw new Error(d.message || 'Failed to load organization teams');
  return d.data || [];
}

export async function resolveOrgGpIdForAssignment(groupsPeriods, contextGpId) {
  if (!contextGpId) return null;
  const gp = (groupsPeriods || []).find((g) => String(g.id) === String(contextGpId));
  if (gp && isOrgShellGp(gp)) return String(gp.id);

  const roots = (groupsPeriods || []).filter(isOrgShellGp);
  if (!roots.length) return null;

  const checks = await Promise.all(
    roots.map(async (root) => {
      try {
        const nested = await fetchNestedGroupsForOrg(root.id);
        const ids = collectGpIdsFromNestedGroups(nested);
        return ids.has(String(contextGpId)) ? String(root.id) : null;
      } catch {
        return null;
      }
    })
  );
  return checks.find(Boolean) || null;
}

export async function loadOrgTeamGpOptions(groupsPeriods, assignmentParentGpId) {
  const orgGpId = await resolveOrgGpIdForAssignment(groupsPeriods, assignmentParentGpId);
  if (!orgGpId) {
    return { orgGpId: null, orgLabel: '', teamGps: [] };
  }
  const orgGp = (groupsPeriods || []).find((g) => String(g.id) === orgGpId);
  const orgLabel = orgGp ? formatGroupsPeriodLabel(orgGp) : 'Organization';
  const nested = await fetchNestedGroupsForOrg(orgGpId);
  return { orgGpId, orgLabel, teamGps: flattenOrgTeamGpOptions(nested) };
}

export function buildWorkSampleAssignmentMetadata({
  studentGpId,
  vdmGpId,
  complianceGpId,
  flagReasons = null
}) {
  const student = String(studentGpId || '').trim();
  const vdm = String(vdmGpId || '').trim();
  const compliance = String(complianceGpId || '').trim();

  if (!student) throw new Error('Select a Students group-period.');
  if (!vdm) throw new Error('Select a VDM / Tech Support group-period.');
  if (!compliance) throw new Error('Select a Compliance group-period.');

  const meta = {
    workflow: WORK_SAMPLE_WORKFLOW_KEY,
    roles: {
      [WORK_SAMPLE_ROLES.STUDENT]: { gp_id: student },
      [WORK_SAMPLE_ROLES.TOR]: {
        via: 'person_links',
        type: TEACHER_OF_RECORD_LINK_TYPE
      },
      [WORK_SAMPLE_ROLES.VDM]: { gp_id: vdm },
      [WORK_SAMPLE_ROLES.COMPLIANCE]: { gp_id: compliance },
      [WORK_SAMPLE_ROLES.VENDOR]: {
        via: 'student_info',
        field: 'vendor_groups_periods_id'
      }
    }
  };

  if (Array.isArray(flagReasons) && flagReasons.length) {
    meta.flag_reasons = flagReasons;
  }

  return meta;
}

function parseRoleGpField(roles, roleKey) {
  const parsed = parseGpRoleRef(roles[roleKey]?.gp_id);
  return {
    gpId: parsed.gpId || '',
    includeDescendants: parsed.includeDescendants
  };
}

export function parseWorkSampleRoleGpIds(metadata) {
  const roles = metadata?.roles && typeof metadata.roles === 'object' ? metadata.roles : {};
  const student = parseRoleGpField(roles, WORK_SAMPLE_ROLES.STUDENT);
  const vdm = parseRoleGpField(roles, WORK_SAMPLE_ROLES.VDM);
  const compliance = parseRoleGpField(roles, WORK_SAMPLE_ROLES.COMPLIANCE);
  return {
    studentGpId: student.gpId,
    studentIncludeDescendants: student.includeDescendants,
    vdmGpId: vdm.gpId,
    vdmIncludeDescendants: vdm.includeDescendants,
    complianceGpId: compliance.gpId,
    complianceIncludeDescendants: compliance.includeDescendants
  };
}
